import { Label, Radio, RadioGroup } from '@blueprintjs/core';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import { TEST_IDS } from 'src/constants/testIds';
import {
  useModelNamePlaceholderByModelId,
  useModelNamedGraphPlaceholderByModelId,
  useValidateModelName,
  useValidateNamedGraphIri,
} from 'src/containers/PublishProjectDrawer/hooks';
import { ModelMappingForm } from 'src/containers/PublishProjectDrawer/ModelMappingForm';
import { ModelMappingRows } from 'src/containers/PublishProjectDrawer/ModelMappingRows';
import { NamedGraphDeletionWarning } from 'src/containers/PublishProjectDrawer/NamedGraphDeletionWarning';
import {
  CreationType,
  ModelInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ModelConfigPanelModelsProps = {
  databaseId: string;
  existingByModelId: Record<string, ModelSelectItem | null>;
  isConsolidating: boolean;
  modelsInfo: ModelsInfo;
  handleChangeCreationTypeForModel: (
    modelId: string,
    value: CreationType
  ) => void;
  handleSelectExistingForModel: (
    modelId: string,
    existing: ModelSelectItem | null
  ) => void;
  handleSetInfoForModel: (
    modelId: string,
    nextInfo: Partial<ModelInfo>
  ) => void;
  handleSetIsConsolidating: (isConsolidating: boolean) => void;
};

export const ModelConfigPanelModels: React.VFC<ModelConfigPanelModelsProps> = ({
  databaseId,
  existingByModelId,
  isConsolidating,
  modelsInfo,
  handleChangeCreationTypeForModel,
  handleSelectExistingForModel,
  handleSetInfoForModel,
  handleSetIsConsolidating,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const modelNamePlaceholderByModelId =
    useModelNamePlaceholderByModelId(databaseId);
  const namedGraphIriPlaceholderByModelId =
    useModelNamedGraphPlaceholderByModelId(databaseId);

  const [tabbedModelId, setTabbedModelId] = React.useState(
    Object.keys(modelsInfo)[0]
  );

  const formModelId = React.useMemo(
    () =>
      isConsolidating ? project?.targetModelId || tabbedModelId : tabbedModelId,
    [isConsolidating, project, tabbedModelId]
  );
  const formModelInfo = React.useMemo(
    () => modelsInfo[formModelId],
    [formModelId, modelsInfo]
  );

  const validateModelName = useValidateModelName({
    databaseId,
    targetModelId: project?.targetModelId,
    isConsolidating,
    modelsInfo,
  });
  const validateNamedGraph = useValidateNamedGraphIri(
    databaseId,
    project?.targetModelId,
    isConsolidating,
    modelsInfo
  );

  const existingDbModel = React.useMemo(
    () => existingByModelId[formModelId],
    [existingByModelId, formModelId]
  );

  const modelNamePlaceholder = modelNamePlaceholderByModelId[formModelId];
  const namedGraphIriPlaceholder =
    namedGraphIriPlaceholderByModelId[formModelId];

  if (Object.keys(modelsInfo).length === 1) {
    return (
      <>
        <div>
          <NamedGraphDeletionWarning
            databaseId={databaseId}
            modelsInfo={modelsInfo}
          />
          <div className="sd-drawer-group-label">
            <Label>
              {
                copy.components.publishProjectDrawer.modelConfiguration
                  .modelConfiguration
              }
            </Label>
          </div>
          <ModelMappingForm
            databaseId={databaseId}
            existingModel={existingDbModel}
            handleChangeCreationType={(creationType) =>
              handleChangeCreationTypeForModel(formModelId, creationType)
            }
            handleSelectExistingModel={(existingModel) =>
              handleSelectExistingForModel(formModelId, existingModel)
            }
            handleSetModelInfo={(info) =>
              handleSetInfoForModel(formModelId, info)
            }
            modelInfo={formModelInfo}
            modelNamePlaceholder={modelNamePlaceholder}
            namedGraphIriPlaceholder={namedGraphIriPlaceholder}
            validateModelName={(modelName) =>
              validateModelName({ ...formModelInfo, modelName })
            }
            validateNamedGraph={(namedGraphIri) =>
              validateNamedGraph({ ...formModelInfo, namedGraphIri })
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="sd-model-selection">
        <div className="sd-drawer-group-label">
          <Label>
            {
              copy.components.publishProjectDrawer.modelConfiguration
                .modelSelection.title
            }
          </Label>
        </div>
        <Text className="sd-model-selection__description">
          {
            copy.components.publishProjectDrawer.modelConfiguration
              .modelSelection.description
          }
        </Text>
        <RadioGroup
          className="sd-model-selection__radio-group sd-drawer-margin"
          onChange={({
            currentTarget: { value },
          }: React.FormEvent<HTMLInputElement>) => {
            if (value === '1') {
              setTabbedModelId(Object.keys(modelsInfo)[0]);
              handleSetIsConsolidating(true);
            } else {
              handleSetIsConsolidating(false);
            }
          }}
          selectedValue={isConsolidating ? 1 : 0}
        >
          <Radio
            data-testid={
              TEST_IDS.publishProjectDrawer.model.multipleModelsRadioButton
            }
            label={
              copy.components.publishProjectDrawer.modelConfiguration
                .modelSelection.multiple
            }
            value={0}
          />
          <Radio
            data-testid={
              TEST_IDS.publishProjectDrawer.model.consolidateRadioButton
            }
            label={
              copy.components.publishProjectDrawer.modelConfiguration
                .modelSelection.consolidate
            }
            value={1}
          />
        </RadioGroup>
      </div>
      <NamedGraphDeletionWarning
        databaseId={databaseId}
        modelsInfo={modelsInfo}
      />
      <div>
        <div className="sd-drawer-group-label">
          <Label>
            {
              copy.components.publishProjectDrawer.modelConfiguration
                .modelConfiguration
            }
          </Label>
        </div>
        {isConsolidating ? (
          <ModelMappingForm
            databaseId={databaseId}
            existingModel={existingDbModel}
            handleChangeCreationType={(creationType) =>
              handleChangeCreationTypeForModel(formModelId, creationType)
            }
            handleSelectExistingModel={(existingModel) =>
              handleSelectExistingForModel(formModelId, existingModel)
            }
            handleSetModelInfo={(info) =>
              handleSetInfoForModel(formModelId, info)
            }
            modelInfo={formModelInfo}
            modelNamePlaceholder={modelNamePlaceholder}
            namedGraphIriPlaceholder={namedGraphIriPlaceholder}
            validateModelName={(modelName) =>
              validateModelName({ ...formModelInfo, modelName })
            }
            validateNamedGraph={(namedGraphIri) =>
              validateNamedGraph({ ...formModelInfo, namedGraphIri })
            }
          />
        ) : (
          <ModelMappingRows
            databaseId={databaseId}
            existingByModelId={existingByModelId}
            handleChangeCreationTypeForModel={handleChangeCreationTypeForModel}
            handleSelectExistingForModel={handleSelectExistingForModel}
            handleSetInfoForModel={handleSetInfoForModel}
            isConsolidating={isConsolidating}
            modelsInfo={modelsInfo}
          />
        )}
      </div>
    </>
  );
};
